// Spell Bridge: a platformer. Every gap is crossed by spelling the word from its clue;
// each right letter lays a plank. Falling just puts you back on the ledge.

import { h, clear } from '@kit/dom';
import { makeSurface } from '@kit/canvas';
import { makeLoop } from '@kit/loop';
import { shuffle, pick } from '@kit/rng';
import { mapFromStrings, moveBody, drawMap, followCamera, makePad, setTile, type Body, type Camera } from '@kit/tilemap';
import { drawSprite, type Facing } from '@avatar/render';
import type { GameModule, GameContext } from '@shell/types';
import { CLUE_WORDS, tierFor, type ClueWord } from '@content/words/clues';

const T = 24;                 // tile px
const ROWS = 9;
const SKY = 0, GROUND = 1, PLANK = 2, FLAG = 3, GEM = 4;
const GRAVITY = 980, JUMP = -340, RUN = 115;

interface Gap { word: ClueWord; x0: number; laid: number; letters: string[]; wrongs: number; hintNext: boolean; t0: number }

function mount(host: HTMLElement, ctx: GameContext) {
  const level = ctx.level;
  const tier = tierFor(level);
  const gapCount = level <= 2 ? 4 : level <= 6 ? 5 : 6;
  const pool = CLUE_WORDS.filter(w => w.tier === tier && w.word.length <= (level <= 3 ? 7 : level <= 6 ? 9 : 14));
  const words = shuffle(ctx.rng, pool.length >= gapCount ? pool : CLUE_WORDS.filter(w => w.tier <= tier)).slice(0, gapCount);

  // build the map: platform, gap(word length), platform ... flag
  const rows: string[] = Array.from({ length: ROWS }, () => '');
  const gaps: Gap[] = [];
  let x = 0;
  const addPlatform = (len: number) => { for (let i = 0; i < len; i++) { for (let r = 0; r < ROWS; r++) rows[r] += r >= ROWS - 2 ? '#' : (r === ROWS - 5 && i % 3 === 1 && x > 4 ? 'o' : '.'); x++; } };
  addPlatform(6);
  for (const w of words) {
    gaps.push({ word: w, x0: x, laid: 0, letters: [], wrongs: 0, hintNext: false, t0: 0 });
    for (let i = 0; i < w.word.length; i++) { for (let r = 0; r < ROWS; r++) rows[r] += '.'; x++; }
    addPlatform(5);
  }
  for (let r = 0; r < ROWS; r++) rows[r] = rows[r].slice(0, -2) + (r === ROWS - 3 ? 'F' : rows[r].slice(-2, -1)) + rows[r].slice(-1);
  const map = mapFromStrings(rows, { '.': SKY, '#': GROUND, '=': PLANK, 'F': FLAG, 'o': GEM }, T, [GROUND, PLANK]);

  const body: Body = { x: T * 1.5, y: (ROWS - 2) * T - 26, w: 13, h: 24, vx: 0, vy: 0 };
  let facing: Facing = 'right';
  let walkT = 0;
  let gemsGot = 0;
  let current = 0;              // index of the gap being spelled
  let attempts = 0, correct = 0;
  let finished = false;
  let safeX = body.x;
  const t0 = performance.now();

  const stage = h('div');
  const clue = h('p.center', { style: { minHeight: '44px', fontWeight: '700', padding: '2px 4px' } });
  const planks = h('div.row', { style: { justifyContent: 'center', gap: '4px', flexWrap: 'wrap', minHeight: '40px' } });
  const letters = h('div.row', { style: { justifyContent: 'center', gap: '6px', flexWrap: 'wrap' } });
  const hintBtn = h('button.btn.small.ghost', { onclick: hint }, 'Hint');
  const progress = h('div.row', { style: { justifyContent: 'space-between' } }, h('span.muted', null, 'Bridge ', h('strong', null, '1'), ` of ${gaps.length}`), hintBtn);
  host.append(progress, stage, clue, planks, letters);
  const S = makeSurface(stage, { height: ROWS * T, draw: false });
  const pad = makePad(host, { action: 'Jump' });
  const cam: Camera = { x: 0, y: 0, w: S.w, h: ROWS * T };

  function setupGap() {
    const g = gaps[current];
    if (!g) { clue.textContent = 'All bridges built. Run to the flag!'; clear(planks); clear(letters); hintBtn.style.visibility = 'hidden'; return; }
    hintBtn.style.visibility = 'visible';
    g.t0 = performance.now();
    const w = g.word.word;
    const decoys = shuffle(ctx.rng, 'abcdefghijklmnopqrstuvwxyz'.split('').filter(c => !w.includes(c))).slice(0, 2);
    g.letters = shuffle(ctx.rng, [...w.split(''), ...decoys]);
    const c1 = g.word.clues[0], c2 = g.word.clues[Math.min(4, g.word.clues.length - 1)];
    clue.textContent = `${c1} ${c2}`;
    (progress.querySelector('strong') as HTMLElement).textContent = String(current + 1);
    renderSpelling();
  }

  function renderSpelling() {
    const g = gaps[current]; if (!g) return;
    clear(planks); clear(letters);
    const w = g.word.word;
    for (let i = 0; i < w.length; i++) {
      planks.append(h('div', { style: { width: '30px', height: '36px', borderRadius: '6px', background: i < g.laid ? '#b48a5a' : 'var(--surface-2)', border: '2px solid var(--line)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '900', textTransform: 'uppercase', color: '#fff' } }, i < g.laid ? w[i] : ''));
    }
    g.letters.forEach((ch, i) => {
      letters.append(h('button.choice', { style: { minWidth: '44px', minHeight: '48px', fontSize: '22px', textTransform: 'uppercase', padding: '0 10px' }, onclick: () => lay(ch, i) }, ch));
    });
  }

  function lay(ch: string, idx: number) {
    const g = gaps[current]; if (!g || finished) return;
    const w = g.word.word;
    const ms = performance.now() - g.t0;
    attempts++;
    if (ch === w[g.laid]) {
      correct++;
      ctx.report({ skill: 'phonics_spelling', level, correct: true, hintUsed: g.hintNext, ms, problemId: `bridge:${w}:${g.laid}` });
      g.hintNext = false;
      g.letters.splice(idx, 1);
      setTile(map, g.x0 + g.laid, ROWS - 2, PLANK);
      g.laid++;
      ctx.sound('pop');
      if (g.laid >= w.length) {
        ctx.sound('ding');
        ctx.say(`${w.toUpperCase()}! Bridge built. Run across.`);
        ctx.speak(w);
        current++;
        setTimeout(setupGap, 400);
      }
      renderSpelling();
    } else {
      g.wrongs++;
      ctx.report({ skill: 'phonics_spelling', level, correct: false, hintUsed: g.hintNext, ms, problemId: `bridge:${w}:${g.laid}` });
      ctx.sound('buzz');
      letters.classList.remove('shake'); void letters.offsetWidth; letters.classList.add('shake');
      ctx.say(g.laid === 0 ? `Not ${ch.toUpperCase()}. What letter does it start with?` : `Not ${ch.toUpperCase()}. The next letter comes after "${w.slice(0, g.laid).toUpperCase()}".`);
      if (g.wrongs >= 2) hint();
    }
  }

  function hint() {
    const g = gaps[current]; if (!g) return;
    g.hintNext = true;
    const next = g.word.word[g.laid];
    ctx.say(`Hint: the next plank is "${next.toUpperCase()}".`);
    for (const b of Array.from(letters.children) as HTMLElement[]) if (b.textContent === next) { b.style.borderColor = '#f5c542'; b.style.boxShadow = '0 0 0 3px #f5c542 inset'; break; }
  }

  function update(dt: number) {
    if (finished) return;
    const want = (pad.right ? 1 : 0) - (pad.left ? 1 : 0);
    body.vx = want * RUN;
    if (want) facing = want > 0 ? 'right' : 'left';
    if (pad.action && body.onGround) { body.vy = JUMP; ctx.sound('tick'); }
    body.vy += GRAVITY * dt;
    if (body.vy > 600) body.vy = 600;
    moveBody(map, body, dt);
    walkT = want ? walkT + dt * 8 : 0;
    if (body.onGround) safeX = body.x;
    // fell into a gap: back to the ledge
    if (body.y > ROWS * T + 10) {
      body.x = Math.max(T, safeX - 4); body.y = (ROWS - 2) * T - body.h - 1; body.vx = 0; body.vy = 0;
      ctx.say('Whoops! Spell the word to build the bridge first.');
    }
    // gems and flag
    const tx = Math.floor((body.x + body.w / 2) / T), ty = Math.floor((body.y + body.h / 2) / T);
    const id = map.data[ty * map.cols + tx];
    if (id === GEM) { setTile(map, tx, ty, SKY); gemsGot++; ctx.sound('pop'); }
    if (id === FLAG || tileRight() === FLAG) finish();
    followCamera(cam, map, body.x + body.w / 2, ROWS * T / 2, 0.2);
  }
  function tileRight(): number { const tx = Math.floor((body.x + body.w + 1) / T), ty = Math.floor((body.y + body.h / 2) / T); return map.data[ty * map.cols + tx]; }

  function finish() {
    if (finished) return;
    finished = true;
    ctx.sound('chime');
    if (attempts === correct) ctx.charm('charm.bridge');
    setTimeout(() => ctx.end({ attempts, correct, perfect: attempts === correct, durationMs: performance.now() - t0, notes: [`Gems collected on the way: ${gemsGot}`] }), 700);
  }

  function draw() {
    const c = S.ctx, W = S.w, H = S.h;
    cam.w = W;
    c.fillStyle = '#bfe3f5'; c.fillRect(0, 0, W, H);
    // distant hills
    c.fillStyle = '#8fd08a';
    for (let i = -1; i < 6; i++) { const hx = i * 160 - (cam.x * 0.3) % 160; c.beginPath(); c.ellipse(hx, H * 0.78, 110, 40, 0, 0, 7); c.fill(); }
    drawMap(c, map, cam, (id, sx, sy, size) => {
      if (id === GROUND) { c.fillStyle = '#9ad67e'; c.fillRect(sx, sy, size, size); c.fillStyle = '#8a5a3b'; c.fillRect(sx, sy + 6, size, size - 6); c.fillStyle = '#b5744a'; c.fillRect(sx + 4, sy + 12, 4, 4); }
      else if (id === PLANK) { c.fillStyle = '#b48a5a'; c.fillRect(sx, sy + 4, size, 10); c.fillStyle = '#8a5a3b'; c.fillRect(sx + 2, sy + 14, 3, size - 14); c.fillRect(sx + size - 5, sy + 14, 3, size - 14); }
      else if (id === FLAG) { c.fillStyle = '#9a9aa3'; c.fillRect(sx + size / 2 - 1, sy - size, 3, size * 2); c.fillStyle = '#e2574c'; c.beginPath(); c.moveTo(sx + size / 2 + 2, sy - size); c.lineTo(sx + size + 8, sy - size + 8); c.lineTo(sx + size / 2 + 2, sy - size + 16); c.fill(); }
      else if (id === GEM) { c.fillStyle = '#4f8fe6'; c.beginPath(); c.moveTo(sx + size / 2, sy + 4); c.lineTo(sx + size - 5, sy + size / 2); c.lineTo(sx + size / 2, sy + size - 4); c.lineTo(sx + 5, sy + size / 2); c.closePath(); c.fill(); }
    });
    // gap labels: the clue word's planks-to-be
    for (let gi = current; gi < gaps.length; gi++) {
      const g = gaps[gi];
      const sx = g.x0 * T - cam.x, sy = (ROWS - 2) * T;
      if (sx > W || sx + g.word.word.length * T < 0) continue;
      c.fillStyle = 'rgba(42,42,46,.25)';
      for (let i = g.laid; i < g.word.word.length; i++) c.fillRect(sx + i * T + 3, sy + 8, T - 6, 4);
      if (gi === current) { c.fillStyle = '#2a2a2e'; c.font = '800 12px Nunito, sans-serif'; c.textAlign = 'center'; c.fillText(`${g.word.word.length} letters`, sx + g.word.word.length * T / 2, sy - 6); }
    }
    const frame = walkT > 0 ? Math.floor(walkT) % 4 : 0;
    drawSprite(c, ctx.avatar, body.x + body.w / 2 - cam.x, body.y + body.h - cam.y, body.h / 32, frame, facing);
  }

  const loop = makeLoop(update, draw);
  loop.start();
  setupGap();
  ctx.say('Walk with the arrows. Spell the word to build each bridge.');

  return { destroy() { loop.stop(); pad.destroy(); S.destroy(); clear(host); } };
}

const mod: GameModule = {
  id: 'words.spell-bridge',
  title: 'Spell Bridge',
  blurb: 'Run and jump. Every gap is a word: spell it to lay the planks.',
  category: 'words',
  skills: ['phonics_spelling', 'vocabulary'],
  levelRange: [1, 10],
  unlock: { stars: 7 },
  mount,
};
export default mod;
